/**
 * Register status (Creditreform "Firmenstatus", TD-2948): the English wording and the order
 * shared by the company page, the result lists, the Add filter panel and the sort menu.
 *
 * The backend passes the value through exactly as Creditreform delivers it, in German, with no
 * enumeration behind it. A value missing from the list below is shown as "Other status" (never
 * in German, never with a badge) and sorts after every listed one. A null status means unknown
 * and must never read as active. The order matches the backend sort (TD-3184), so agent, MCP
 * and web list companies the same way.
 */

export const REGISTER_STATUS_UNKNOWN = 'Unknown';
export const REGISTER_STATUS_OTHER = 'Other status';

export const REGISTER_STATUSES = [
  { values: ['aktiv'], name: 'Active', active: true },
  { values: ['neugründung'], name: 'Newly founded', active: true },
  { values: ['in gründung'], name: 'In formation', active: true },
  {
    values: ['eingestellt / ruhend', 'eingestellt', 'ruhend'],
    name: 'Ceased operations / dormant',
    active: false
  },
  { values: ['in liquidation'], name: 'In liquidation', active: false },
  { values: ['löschungsankündigung'], name: 'Deletion pending', active: false },
  { values: ['aufgelöst'], name: 'Dissolved', active: false },
  { values: ['erloschen'], name: 'Deleted', active: false },
  { values: ['abgemeldet'], name: 'Trade registration cancelled', active: false },
  {
    values: ['übernommen / verschmolzen', 'übernommen', 'verschmolzen'],
    name: 'Acquired / merged',
    active: false
  }
].map((status, index) => ({ ...status, rank: index + 1 }));

// An unlisted value comes after every listed one in both sort directions, so it needs its own
// rank per direction: the highest for "active first" (ascending), the lowest for "non-active
// first" (descending). Unknown has no rank in either; the list sorts put it last in both.
const OTHER_RANK_ACTIVE_FIRST = REGISTER_STATUSES.length + 1;
const OTHER_RANK_NON_ACTIVE_FIRST = 0.5;

const APPROXIMATE_PRECISIONS = new Set(['estimated', 'approximate']);

// NFC first: an umlaut can arrive decomposed ("o" plus a combining mark) and would then match
// nothing in the list above.
const normalise = (value) =>
  typeof value === 'string' ? value.normalize('NFC').trim().toLowerCase() : '';

const findStatus = (value) => {
  const key = normalise(value);
  return key ? REGISTER_STATUSES.find((status) => status.values.includes(key)) : undefined;
};

/** English name; "Other status" when the value is not listed; null when unknown. */
export const getRegisterStatusName = (value) => {
  if (!normalise(value)) return null;
  return findStatus(value)?.name ?? REGISTER_STATUS_OTHER;
};

/** Rank for "active first", sorted ascending (1 = Active); null when unknown. */
export const getRegisterStatusRank = (value) => {
  if (!normalise(value)) return null;
  return findStatus(value)?.rank ?? OTHER_RANK_ACTIVE_FIRST;
};

/** Rank for "non-active first", sorted descending: the listed rank; null when unknown. */
export const getRegisterStatusNonActiveRank = (value) => {
  if (!normalise(value)) return null;
  return findStatus(value)?.rank ?? OTHER_RANK_NON_ACTIVE_FIRST;
};

/**
 * Derives the filter value and both sort ranks from the raw status on a company row. Called by
 * mapCompanyEntry for every list read, and again by the company page when it writes a fresher
 * status onto the row it opened, so the badge, the filter and the sorts always agree.
 */
export const applyRegisterStatus = (company) => {
  company.registerStatus = [
    getRegisterStatusName(company.companyStatus) ?? REGISTER_STATUS_UNKNOWN
  ];
  company._registerStatusRank = getRegisterStatusRank(company.companyStatus);
  company._registerStatusNonActiveRank = getRegisterStatusNonActiveRank(company.companyStatus);
};

/** True only for a listed status that means the company no longer trades normally. */
export const isNonActiveRegisterStatus = (value) => findStatus(value)?.active === false;

/**
 * Whether the list read delivered a status for any of these mapped rows. Reads the flag only
 * mapCompanyEntry sets, not the status: the company page can later write a status onto a row it
 * opened (supplementary data), and that one row must not turn the filter and sorts on for a
 * list whose read carried no status at all.
 */
export const hasKnownRegisterStatus = (companies = []) =>
  companies.some((company) => company?._registerStatusFromList === true);

/**
 * "since 06/2025", or "since 2025" for a 1 January date. Day and month may be fill-ins (the
 * source date is fuzzy and the ETL pads missing parts with 01), so the day is never shown and
 * a padded month is not presented as known. Parsed from the string to stay clear of time-zone
 * shifts.
 */
export const formatRegisterStatusSince = (date, precision) => {
  const match = typeof date === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(date) : null;
  if (!match) return '';
  const [, year, month, day] = match;
  const approx = APPROXIMATE_PRECISIONS.has(normalise(precision)) ? 'approx. ' : '';
  const shown = month === '01' && day === '01' ? year : `${month}/${year}`;
  return `since ${approx}${shown}`;
};

/** "Dissolved since 06/2025", "Active" or '' for a company with an unknown status. */
export const getRegisterStatusText = (company) => {
  const name = getRegisterStatusName(company?.companyStatus);
  if (!name) return '';
  const since = formatRegisterStatusSince(
    company.companyStatusSince,
    company.companyStatusSincePrecision
  );
  return since ? `${name} ${since}` : name;
};
